import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1762716514222-996318bb41a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBuZW9uJTIwc3BoZXJlfGVufDB8fHx8MTc4NTg4ODQyNHww&ixlib=rb-4.1.0&q=85";

export const useVisionImages = () => {
  const [images, setImages] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vision/images");
      setImages(data.images);
    } catch (err) {
      console.error("Vision images load failed:", err); // fallback image used
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { images, reload: load };
};

export const resolveUrl = (img) =>
  img.external_url ? img.external_url : `${process.env.REACT_APP_BACKEND_URL}/api/vision/file/${img.storage_path}`;

export const VisionSlideshow = ({ images, intervalMs = 5000, className = "" }) => {
  const [index, setIndex] = useState(0);
  const slides = images.length ? images : [{ id: "fallback", external_url: FALLBACK, category: "", caption: "" }];

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(t);
  }, [slides.length, intervalMs]);

  useEffect(() => { setIndex(0); }, [images.length]);

  const current = slides[index % slides.length];

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`} data-testid="vision-slideshow">
      <AnimatePresence mode="sync">
        <motion.img
          key={current.id}
          src={resolveUrl(current)}
          alt={current.caption || current.category || "Vision"}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      {current.category && (
        <motion.span
          key={`label-${current.id}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="absolute bottom-4 right-4 z-10 text-[9px] uppercase tracking-[0.3em] text-white/70 bg-black/50 backdrop-blur-md border border-white/10 px-3 py-1.5 rounded-full"
          data-testid="vision-slide-label"
        >
          {current.category}{current.caption ? ` — ${current.caption}` : ""}
        </motion.span>
      )}
    </div>
  );
};
